import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule } from '@angular/forms';

// Material
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es-MX';
import {
  MatNativeDateModule,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';

// ngrx
import { StoreModule } from '@ngrx/store';

import { EffectsModule } from '@ngrx/effects';
// ngrx devtools
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

// animations
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// toastr module
import { ToastContainerModule, ToastrModule } from 'ngx-toastr';
import { TourNgxBootstrapModule } from 'ngx-ui-tour-ngx-bootstrap';

import { ApmErrorHandler, ApmModule } from '@elastic/apm-rum-angular';
// Error handler
import { GlobalErrorHandler } from 'src/shared/error-handler/globalErrorHandler.class';

// Solace
import { SharedSolaceModule } from 'src/solace';

import { CoachmarkComponent } from 'src/components/common/coachmark/coachmark.component';
import { CustomDialogComponent } from 'src/components/common/customDialog/customDialog.component';
// app
import { AppComponent } from 'src/app/app.component';
import { Configurations } from 'src/app/app.configurations';
import { AppReducer } from 'src/app/state/app.reducer';

// core services
import { CoreModule } from 'src/core';

// routes
import { AppRoutingModule } from './app-routing.module';

import { AuthInterceptor } from '@http/auth-interceptor';
import { ContentInterceptor } from '@http/content-interceptor';
import { ErrorInterceptor } from '@http/error-interceptor';
import { GeolocationInterceptor } from '@http/geolocation-interceptor';
import { NewRelicLogger } from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import { NumberOnlyModule } from 'src/core/directives/onlyNumbers/onlyNumber.modules';
import { RootModule } from 'src/containers/main/root.module';
import { WindowRef } from 'src/shared/utility/window-ref';
import { NgClickOutsideModule } from 'ng-click-outside2';
import { environment } from 'src/environments/environment';
import { LoggerService } from 'src/components/shared/logger/logger.service';

const MY_DATE_FORMATS: any = {
  parse: {
    dateInput: { year: 'numeric', month: 'short', day: 'numeric' },
  },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'long' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

registerLocaleData(localeEs, 'es-MX');

export function myReducerFactory(reducers: any, initState: any) {
  return (state = AppReducer.appStateFactory, action: any) =>
    AppReducer.getReducer(state, action);
}

@NgModule({
  imports: [
    // Angular
    BrowserModule,
    FormsModule,
    AppRoutingModule,
    // Material
    MatNativeDateModule,
    BrowserAnimationsModule,
    // NgRx
    StoreModule.forRoot(undefined, { reducerFactory: myReducerFactory }),
    EffectsModule.forRoot([]),
    // We use the spread operator on either [StoreDevTools] or [] to prevent
    // falsy values if StoreDevTools should be skipped
    ...(environment.useDevTools
      ? [
          StoreDevtoolsModule.instrument({
            maxAge: 5,
          }),
        ]
      : []),
    // toastr
    ToastrModule.forRoot(),
    ToastContainerModule,
    TourNgxBootstrapModule.forRoot(),
    // Solace
    SharedSolaceModule,
    // App
    CoreModule.forRoot(),
    NumberOnlyModule,
    MatDialogModule,
    HttpClientModule,
    RootModule,
    NgClickOutsideModule,
    ApmModule,
  ],
  declarations: [AppComponent, CustomDialogComponent, CoachmarkComponent],
  exports: [],
  providers: [
    Configurations,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: LOCALE_ID,
      useValue: 'es-MX',
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'es-MX',
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS,
    },
    { provide: WindowRef, useClass: WindowRef },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ContentInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GeolocationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ApmErrorHandler,
    },
    NewRelicLogger,
    {
      provide: NewRelicLogger,
      useClass: LoggerService,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
